"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import type { AlertRuleCreateDto } from "@/lib/api/types";
import { createAlertRule } from "@/lib/api/endpoints/alerts";
import { measureSync } from "@/lib/perf/interaction";
import { useRealtimeStore } from "@/store/realtime";

import { alertsGateway, alertsQueryKeys, type AlertsListQueryKeyParams } from "./api";
import {
  filterAlertsBySeverity,
  filterAlertsByStatus,
  mergeHistoryAndStream,
} from "./utils/merge-alerts-feed";

export function useAlertsListQuery(key: AlertsListQueryKeyParams, enabled: boolean) {
  return useQuery({
    queryKey: alertsQueryKeys.list(key),
    queryFn: () =>
      alertsGateway.fetchList({
        tenantId: key.tenantId,
        deviceId: key.deviceId,
        workspaceScope: key.workspaceScope,
        status: key.status,
        severity: key.severity,
        start: key.start,
        end: key.end,
      }),
    enabled,
    staleTime: 15_000,
  });
}

export function useAlertsFeed(options: {
  tenantId?: string;
  deviceId?: string;
  workspaceScope?: "all";
  severity: string | null;
  status?: "active" | "resolved" | null;
  start?: string;
  end?: string;
  enabled: boolean;
}) {
  const list = useAlertsListQuery(
    {
      tenantId: options.tenantId,
      deviceId: options.deviceId,
      workspaceScope: options.workspaceScope,
      status: options.status ?? undefined,
      severity: options.severity ?? undefined,
      start: options.start,
      end: options.end,
    },
    options.enabled,
  );
  const stream = useRealtimeStore((s) => s.alertsStream);

  const alerts = useMemo(() => {
    const merged = measureSync("alerts.mergeHistoryAndStream", () =>
      mergeHistoryAndStream(list.data ?? [], stream),
    );
    const bySev = filterAlertsBySeverity(merged, options.severity);
    return filterAlertsByStatus(bySev, options.status ?? null);
  }, [list.data, stream, options.severity, options.status]);

  return { ...list, alerts };
}

export function useAlertDetail(alertId: string, enabled: boolean) {
  return useQuery({
    queryKey: alertsQueryKeys.detail(alertId),
    queryFn: () => alertsGateway.fetchDetail(alertId),
    enabled: enabled && alertId.length > 0,
    staleTime: 15_000,
  });
}

export function useCreateAlertRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AlertRuleCreateDto) => createAlertRule(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: alertsQueryKeys.all });
    },
  });
}
